import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Check, ListPlus, Pencil, Plus, Trash2, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  cargarEtapasDePlantilla,
  deshacerEtapa,
  eliminarEtapa,
  moverEtapa,
  type EtapaImportacion,
  type ImportacionDetalle,
} from '@/shared/api/controllers/importaciones';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { cn } from '@/shared/lib/cn';
import { CompletarEtapaDialog, EtapaDialog } from './EtapaDialog';
import { EstadoEtapaBadge, IconAction, fechaImp } from './componentes';

const PUNTO: Record<EtapaImportacion['estado'], string> = {
  completada: 'bg-emerald-500',
  vencida: 'bg-rose-500',
  en_curso: 'bg-sky-500',
  pendiente: 'bg-slate-300',
};

/**
 * Ciclo de vida de la importación: etapas con su duración, fechas planificadas
 * encadenadas y fecha real al completarlas. Editable sólo mientras la
 * importación está abierta; cerrarla sigue siendo manual (acá sólo se sugiere).
 */
export function EtapasImportacion({ imp, puedeEditar }: { imp: ImportacionDetalle; puedeEditar: boolean }) {
  const qc = useQueryClient();
  const [dialogo, setDialogo] = useState<{ etapa: EtapaImportacion | null } | null>(null);
  const [completando, setCompletando] = useState<EtapaImportacion | null>(null);
  const [eliminando, setEliminando] = useState<EtapaImportacion | null>(null);

  const refrescar = () => qc.invalidateQueries({ queryKey: ['importaciones'] });
  const fallo = (e: unknown) => toast.error(e instanceof Error ? e.message : 'No se pudo actualizar la etapa.');

  const deshacer = useMutation({
    mutationFn: (e: EtapaImportacion) => deshacerEtapa(imp.id, e.id),
    onSuccess: refrescar,
    onError: fallo,
  });
  const mover = useMutation({
    mutationFn: ({ etapa, direccion }: { etapa: EtapaImportacion; direccion: 'subir' | 'bajar' }) =>
      moverEtapa(imp.id, etapa.id, direccion),
    onSuccess: refrescar,
    onError: fallo,
  });
  const cargar = useMutation({
    mutationFn: () => cargarEtapasDePlantilla(imp.id),
    onSuccess: refrescar,
    onError: fallo,
  });
  const eliminar = useMutation({
    mutationFn: (e: EtapaImportacion) => eliminarEtapa(imp.id, e.id),
    onSuccess: () => {
      refrescar();
      setEliminando(null);
    },
    onError: (e) => {
      setEliminando(null);
      fallo(e);
    },
  });

  const etapas = imp.etapas;
  const completas = etapas.filter((e) => e.estado === 'completada').length;
  const todasCompletas = etapas.length > 0 && completas === etapas.length;
  const ocupado = deshacer.isPending || mover.isPending || cargar.isPending;

  return (
    <section className="shrink-0 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-sm font-semibold">
          Etapas{etapas.length > 0 ? ` (${completas}/${etapas.length} completas)` : ''}
        </h2>
        {puedeEditar && etapas.length > 0 && (
          <Button variant="outline" size="sm" className="ml-auto gap-1.5" onClick={() => setDialogo({ etapa: null })}>
            <Plus className="h-4 w-4" aria-hidden /> Agregar etapa
          </Button>
        )}
      </div>

      {todasCompletas && imp.status === 'Abierta' && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          Todas las etapas están completas. Si ya no quedan compras ni pagos por cargar, podés cerrar la importación.
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border bg-card">
        {etapas.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              Esta importación no tiene etapas. Con ellas el sistema calcula la fecha de llegada y avisa cuando una
              está por vencer.
            </p>
            {puedeEditar && (
              <div className="flex flex-wrap justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={cargar.isPending}
                  onClick={() => cargar.mutate()}
                >
                  <ListPlus className="h-4 w-4" aria-hidden /> Cargar etapas desde la plantilla
                </Button>
                <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setDialogo({ etapa: null })}>
                  <Plus className="h-4 w-4" aria-hidden /> Agregar etapa
                </Button>
              </div>
            )}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Etapa</th>
                <th className="px-4 py-2 text-right font-medium">Días</th>
                <th className="px-4 py-2 font-medium">Planificada</th>
                <th className="px-4 py-2 font-medium">Fecha real</th>
                <th className="px-4 py-2 font-medium">Estado</th>
                {puedeEditar && <th className="px-4 py-2 text-right font-medium">Acciones</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {etapas.map((e, i) => (
                <tr key={e.id} className={cn(e.actual && 'bg-muted/30')}>
                  <td className="px-4 py-2">
                    <span className="flex items-center gap-2">
                      <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', PUNTO[e.estado])} aria-hidden />
                      <span
                        className={cn('font-medium', e.estado === 'completada' ? 'text-muted-foreground' : 'text-foreground')}
                      >
                        {e.nombre}
                      </span>
                      {e.esArribo && (
                        <span
                          className="rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                          title="Define la fecha probable de llegada"
                        >
                          Arribo
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{e.dias}</td>
                  <td className="whitespace-nowrap px-4 py-2 tabular-nums text-muted-foreground">
                    {e.dias === 0 ? fechaImp(e.fin) : `${fechaImp(e.inicio)} → ${fechaImp(e.fin)}`}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 tabular-nums">
                    {e.fechaReal ? (
                      puedeEditar ? (
                        <button
                          type="button"
                          className="underline-offset-2 hover:underline"
                          title="Cambiar la fecha real"
                          onClick={() => setCompletando(e)}
                        >
                          {fechaImp(e.fechaReal)}
                        </button>
                      ) : (
                        fechaImp(e.fechaReal)
                      )
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <EstadoEtapaBadge estado={e.estado} />
                  </td>
                  {puedeEditar && (
                    <td className="whitespace-nowrap px-4 py-1.5 text-right">
                      <span className="inline-flex items-center gap-1">
                        {e.fechaReal ? (
                          <IconAction
                            title="Deshacer: volver a pendiente"
                            disabled={ocupado}
                            onClick={() => deshacer.mutate(e)}
                          >
                            <Undo2 className="h-4 w-4" aria-hidden />
                          </IconAction>
                        ) : (
                          <IconAction title="Completar" disabled={ocupado} onClick={() => setCompletando(e)}>
                            <Check className="h-4 w-4" aria-hidden />
                          </IconAction>
                        )}
                        <IconAction title="Editar" disabled={ocupado} onClick={() => setDialogo({ etapa: e })}>
                          <Pencil className="h-4 w-4" aria-hidden />
                        </IconAction>
                        <IconAction
                          title="Subir"
                          disabled={ocupado || i === 0}
                          onClick={() => mover.mutate({ etapa: e, direccion: 'subir' })}
                        >
                          <ArrowUp className="h-4 w-4" aria-hidden />
                        </IconAction>
                        <IconAction
                          title="Bajar"
                          disabled={ocupado || i === etapas.length - 1}
                          onClick={() => mover.mutate({ etapa: e, direccion: 'bajar' })}
                        >
                          <ArrowDown className="h-4 w-4" aria-hidden />
                        </IconAction>
                        <IconAction title="Eliminar" destructive disabled={ocupado} onClick={() => setEliminando(e)}>
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </IconAction>
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {puedeEditar && (
        <>
          <EtapaDialog
            importacionId={imp.id}
            etapas={etapas}
            etapa={dialogo?.etapa ?? null}
            open={dialogo != null}
            onClose={() => setDialogo(null)}
          />
          <CompletarEtapaDialog importacionId={imp.id} etapa={completando} onClose={() => setCompletando(null)} />
        </>
      )}

      <Dialog open={eliminando != null} onOpenChange={(o) => !o && !eliminar.isPending && setEliminando(null)}>
        <DialogContent className="max-w-md" processing={eliminar.isPending && 'Eliminando…'}>
          <DialogHeader>
            <DialogTitle>Eliminar etapa</DialogTitle>
            <DialogDescription>
              {eliminando &&
                `Se elimina "${eliminando.nombre}" de esta importación. Las fechas de las etapas siguientes se recalculan.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEliminando(null)} disabled={eliminar.isPending}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => eliminando && eliminar.mutate(eliminando)}
              disabled={eliminar.isPending}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
